import React, { useContext, useEffect, useState } from 'react';
import { Box, Grid, Typography, IconButton, useMediaQuery, useTheme, Container } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useSnackbar } from '../../SnackbarProvider';
import { FeedbackCTA } from '../../feedback-cta';
import GradientBanner from '../../../UIControls/GradientBanner';
import { recoverWhiteboard } from '../../../helper/whiteboardUtils';
import { recoverCustomisedHandout } from '../../../helper/customisedHandoutUtils';
import WhiteboardCard from './WhiteboardCard';
import CustomisedHandoutCard from '../customise-mode/CustomisedHandoutCard';

const ArchivedHandouts = () => {
    const theme = useTheme();
    const { whiteboardStore, authStore, userDataStore, customisedHandoutStore } = useContext(StoreContext);
    const [archivedWhiteboards, setArchivedWhiteboards] = useState([]);
    const [archivedCustomisedHandouts, setArchivedCustomisedHandouts] = useState([]);
    const [combinedItems, setCombinedItems] = useState([]); // Combined archived items
    const [filtered, setFiltered] = useState([]);
    const [filterType, setFilterType] = useState('all'); // 'all', 'whiteboards', 'customisedHandouts'
    const [page, setPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    const [imageMap, setImageMap] = useState({});
    const [sortDirection, setSortDirection] = useState('desc');
    const [hasSortedRecently, setHasSortedRecently] = useState(false);

    const whiteboardsPerPage = 6;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const showSnackbar = useSnackbar();
    const navigate = useNavigate();

    const paginatedWhiteboards = filtered.slice(page * whiteboardsPerPage, (page + 1) * whiteboardsPerPage);

    // Helper function to get image source for customised handout
    const getCustomisedHandoutImageSrc = (customisedHandout) => {
        if (customisedHandout.attachedWhiteboardCanvasImageLink) {
            return customisedHandout.attachedWhiteboardCanvasImageLink;
        }

        const locale = 'en-UK';
        const contentData = customisedHandout.effectiveContentData?.[locale];
        if (!contentData || !Array.isArray(contentData)) {
            return null;
        }

        for (const section of contentData) {
            if (section.blocks) {
                const illustrationBlock = section.blocks.find((block) => block.type === 'illustration');
                if (illustrationBlock?.src) {
                    return illustrationBlock.src;
                }
            }
        }

        return null;
    };

    useEffect(() => {
        if (!whiteboardStore.isInitialized) return;

        const data = Object.values(whiteboardStore.whiteboardData).filter((wb) => wb.whiteboardStatus === 'archived');

        setArchivedWhiteboards(data);
    }, [whiteboardStore.whiteboardData, whiteboardStore.isInitialized]);

    // Load archived customised handouts
    useEffect(() => {
        if (!customisedHandoutStore?.isInitialized) return;

        const data = Object.values(customisedHandoutStore.customisedHandoutData).filter(
            (ch) => ch.customisedStatus === 'archived',
        );

        setArchivedCustomisedHandouts(data);
    }, [customisedHandoutStore?.customisedHandoutData, customisedHandoutStore?.isInitialized]);

    // Combine archived whiteboards and customised handouts
    useEffect(() => {
        let combined = [
            ...archivedWhiteboards.map((wb) => ({ ...wb, type: 'whiteboard' })),
            ...archivedCustomisedHandouts.map((ch) => ({ ...ch, type: 'customisedHandout' })),
        ].sort((a, b) => b.lastUpdated - a.lastUpdated);

        setCombinedItems(combined);

        // Apply filter
        if (filterType === 'whiteboards') {
            combined = combined.filter((item) => item.type === 'whiteboard');
        } else if (filterType === 'customisedHandouts') {
            combined = combined.filter((item) => item.type === 'customisedHandout');
        }

        setFiltered(combined);
        setPage(0); // Reset to first page when filter changes
    }, [archivedWhiteboards, archivedCustomisedHandouts, filterType]);

    const handleNextPage = () => {
        if ((page + 1) * whiteboardsPerPage < filtered.length) {
            setPage(page + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 0) {
            setPage(page - 1);
        }
    };
    const handleOpenMenu = (event) => {
        const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
        setSortDirection(newDirection);
        setHasSortedRecently(true);

        const sorted = [...filtered].sort((a, b) =>
            newDirection === 'asc' ? a.lastUpdated - b.lastUpdated : b.lastUpdated - a.lastUpdated,
        );
        setFiltered(sorted);
        setPage(0);
    };

    useEffect(() => {
        const fetchImages = async () => {
            const images = {};
            for (const item of filtered) {
                if (item.type === 'whiteboard' && item.canvasImageLink) {
                    const image = await whiteboardStore.imageStore.getImageFromIndexedDB(item.canvasImageLink);
                    if (image?.data) images[item.canvasImageLink] = image.data;
                } else if (item.type === 'customisedHandout') {
                    const illustrationSrc = getCustomisedHandoutImageSrc(item);
                    if (illustrationSrc) {
                        const image = await whiteboardStore.imageStore.getImageFromIndexedDB(illustrationSrc);
                        if (image?.data) images[illustrationSrc] = image.data;
                    }
                }
            }
            setImageMap(images);
        };
        fetchImages();
    }, [filtered]);

    const handleSearchInputChange = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        setPage(0); // reset to first page on new search

        const result = combinedItems.filter((item) => {
            if (item.type === 'whiteboard') {
                return (
                    item.handoutContentName?.toLowerCase().includes(term) ||
                    item.handoutAlphabeticalName?.toLowerCase().includes(term) ||
                    item.handoutDocumentTitle?.toLowerCase().includes(term) ||
                    item.notes?.toLowerCase().includes(term) ||
                    item.fileTitle?.toLowerCase().includes(term) ||
                    item.patientName?.toLowerCase().includes(term)
                );
            } else if (item.type === 'customisedHandout') {
                const locale = 'en-UK';
                const contentData = item.effectiveContentData?.[locale];
                let documentTitle = '';
                if (contentData && Array.isArray(contentData) && contentData.length > 0) {
                    const firstSection = contentData[0];
                    if (firstSection && firstSection.blocks) {
                        const titleBlock = firstSection.blocks.find((block) => block.type === 'documentTitle');
                        documentTitle = titleBlock?.value || '';
                    }
                }
                return documentTitle.toLowerCase().includes(term) || item.notes?.toLowerCase().includes(term);
            }
            return false;
        });

        setFiltered(result);
    };

    const handleRecover = (wb) =>
        recoverWhiteboard(
            wb,
            userDataStore,
            whiteboardStore,
            authStore,
            setArchivedWhiteboards,
            setFiltered,
            showSnackbar,
        );

    const handleRecoverCustomisedHandout = (ch) =>
        recoverCustomisedHandout(
            ch,
            userDataStore,
            customisedHandoutStore,
            authStore,
            setArchivedCustomisedHandouts,
            setFiltered,
            showSnackbar,
        );

    return (
        <>
            <GradientBanner
                title="Archived Handouts"
                subtitle="Review and recover your archived whiteboards and customised handouts"
                placeholder="Search archived handouts..."
                searchTerm={searchTerm}
                onSearchChange={handleSearchInputChange}
                isMobile={isMobile}
                isTablet={isTablet}
                height={isMobile ? 'initial' : '450px'}
                paddingY={isMobile ? '48px' : '40px'}
                paddingBottom="40px"
                className="search-banner"
            />
            <Container
                component={'section'}
                maxWidth={'xl'}
                sx={{ backgroundColor: '#F0F0FC', px: isMobileOrTablet ? 2 : 6, pt: 3 }}
            >
                {/* Filter and Sort Controls */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent:
                            userDataStore?.subscriptionData?.accountType === 'Student' ? 'flex-end' : 'space-between',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        mt: 2,
                        pr: isMobile ? 0 : 2,
                        mb: isMobile ? 2 : '67px',
                        paddingX: isMobile ? '48px' : '66px',
                        gap: isMobile ? 1.5 : 2,
                    }}
                >
                    {/* Filter Radio Buttons - Hidden for Student plan */}
                    {userDataStore?.subscriptionData?.accountType !== 'Student' && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: isMobile ? 1.5 : 3,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                width: isMobile ? '100%' : 'auto',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'Inter',
                                    fontWeight: 600,
                                    fontSize: isMobile ? '12px' : '14px',
                                    lineHeight: '14px',
                                    letterSpacing: '2px',
                                    textTransform: 'uppercase',
                                    color: '#1919194D',
                                    flexShrink: 0,
                                }}
                            >
                                SHOW:
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: isMobile ? 1.5 : 2,
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box
                                    onClick={() => setFilterType('all')}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: isMobile ? 14 : 16,
                                            height: isMobile ? 14 : 16,
                                            borderRadius: '50%',
                                            border: '2px solid #000453',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: filterType === 'all' ? '#000453' : 'transparent',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {filterType === 'all' && (
                                            <Box
                                                sx={{
                                                    width: isMobile ? 6 : 8,
                                                    height: isMobile ? 6 : 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'white',
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter',
                                            fontSize: isMobile ? '12px' : '14px',
                                            color: filterType === 'all' ? '#000453' : '#1919194D',
                                            fontWeight: filterType === 'all' ? 600 : 500,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        All
                                    </Typography>
                                </Box>

                                <Box
                                    onClick={() => setFilterType('whiteboards')}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: isMobile ? 14 : 16,
                                            height: isMobile ? 14 : 16,
                                            borderRadius: '50%',
                                            border: '2px solid #000453',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: filterType === 'whiteboards' ? '#000453' : 'transparent',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {filterType === 'whiteboards' && (
                                            <Box
                                                sx={{
                                                    width: isMobile ? 6 : 8,
                                                    height: isMobile ? 6 : 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'white',
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter',
                                            fontSize: isMobile ? '12px' : '14px',
                                            color: filterType === 'whiteboards' ? '#000453' : '#1919194D',
                                            fontWeight: filterType === 'whiteboards' ? 600 : 500,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Whiteboards
                                    </Typography>
                                </Box>

                                <Box
                                    onClick={() => setFilterType('customisedHandouts')}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: isMobile ? 14 : 16,
                                            height: isMobile ? 14 : 16,
                                            borderRadius: '50%',
                                            border: '2px solid #000453',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor:
                                                filterType === 'customisedHandouts' ? '#000453' : 'transparent',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {filterType === 'customisedHandouts' && (
                                            <Box
                                                sx={{
                                                    width: isMobile ? 6 : 8,
                                                    height: isMobile ? 6 : 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'white',
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter',
                                            fontSize: isMobile ? '12px' : '14px',
                                            color: filterType === 'customisedHandouts' ? '#000453' : '#1919194D',
                                            fontWeight: filterType === 'customisedHandouts' ? 600 : 500,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Customised Handouts
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Sort By Section */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: isMobile ? '100%' : 'auto',
                            justifyContent: isMobile ? 'flex-start' : 'flex-end',
                        }}
                    >
                        <Typography
                            onClick={handleOpenMenu}
                            sx={{
                                fontFamily: 'Inter',
                                fontWeight: 600,
                                fontSize: '14px',
                                lineHeight: '14px',
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                color: '#1919194D',
                                cursor: 'pointer',
                            }}
                        >
                            SORT BY{' '}
                            <Typography component="span" sx={{ color: '#000453' }}>
                                RECENT
                            </Typography>
                            <IconButton size="small" sx={{ ml: 1, p: 0 }}>
                                {sortDirection === 'desc' ? (
                                    <ExpandMoreIcon sx={{ color: '#000453' }} />
                                ) : (
                                    <ExpandLessIcon sx={{ color: '#000453' }} />
                                )}
                            </IconButton>
                        </Typography>
                    </Box>
                </Box>

                {filtered.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', color: '#000453' }}>No archived handouts found.</Typography>
                ) : (
                    <Grid
                        container
                        spacing={3}
                        px={isMobile ? '48px' : '66px'}
                        sx={{ position: 'relative', ml: '0px!important', width: '100%' }}
                    >
                        {paginatedWhiteboards.map((item, index) => {
                            if (item.type === 'whiteboard') {
                                return (
                                    <WhiteboardCard
                                        key={item.whiteboardId || index}
                                        whiteboard={item}
                                        isMobile={isMobile}
                                        userDataStore={userDataStore}
                                        authStore={authStore}
                                        whiteboardStore={whiteboardStore}
                                        showSnackbar={showSnackbar}
                                        imageMap={imageMap}
                                        index={index}
                                        allowAddNote={false}
                                        actionLabel="Recover"
                                        confirmTitle="Are you sure you want to recover this whiteboard?"
                                        confirmBody=""
                                        handleToggleFavourite={() => {}}
                                        handleEditWhiteboard={() => {
                                            sessionStorage.setItem(
                                                'lastWhiteboardState',
                                                JSON.stringify({
                                                    whiteboardId: item.whiteboardId,
                                                    handoutId: item.handoutId,
                                                    canvasImageLink: item.canvasImageLink,
                                                    canvasJsonLink: item.canvasJsonLink,
                                                    fileName: item.fileName || '',
                                                    eTag: item.eTag,
                                                }),
                                            );
                                            navigate(`/handout/${item.handoutId}`);
                                        }}
                                        handleArchiveWhiteboard={() => handleRecover(item)}
                                        hideEdit={true}
                                        hideFavourite={true}
                                    />
                                );
                            } else if (item.type === 'customisedHandout') {
                                return (
                                    <CustomisedHandoutCard
                                        key={item.customisedId || index}
                                        customisedHandout={item}
                                        isMobile={isMobile}
                                        userDataStore={userDataStore}
                                        showSnackbar={showSnackbar}
                                        handleToggleFavourite={() => {}}
                                        handleEditCustomisedHandout={() => {
                                            localStorage.setItem('loadCustomisedHandout', item.customisedId);
                                            localStorage.setItem('loadCustomisedHandoutInEditMode', 'true');
                                            navigate(`/handout/${item.baseContentId}`);
                                        }}
                                        handleArchiveCustomisedHandout={() => handleRecoverCustomisedHandout(item)}
                                        imageMap={imageMap}
                                        index={index}
                                        actionLabel="Recover"
                                        confirmTitle="Are you sure you want to recover this customised handout?"
                                        confirmBody=""
                                        hideEdit={true}
                                        hideFavourite={true}
                                    />
                                );
                            }
                            return null;
                        })}
                        {/* Pagination Controls */}
                        {filtered.length > 6 && (
                            <>
                                <IconButton
                                    onClick={handlePrevPage}
                                    disabled={page === 0}
                                    sx={{
                                        position: 'absolute',
                                        left: '-10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 10,
                                        backgroundColor: 'rgba(0, 4, 83, 0.08)',
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 4, 83, 0.12)',
                                        },
                                    }}
                                >
                                    <ChevronLeftRoundedIcon sx={{ fontSize: '32px', color: '#000453' }} />
                                </IconButton>

                                <IconButton
                                    onClick={handleNextPage}
                                    disabled={(page + 1) * whiteboardsPerPage >= filtered.length}
                                    sx={{
                                        position: 'absolute',
                                        right: '-10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 10,
                                        backgroundColor: 'rgba(0, 4, 83, 0.08)',
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 4, 83, 0.12)',
                                        },
                                    }}
                                >
                                    <ChevronRightRoundedIcon sx={{ fontSize: '32px', color: '#000453' }} />
                                </IconButton>
                            </>
                        )}
                    </Grid>
                )}
            </Container>

            {/* Feedback Section */}
            <FeedbackCTA />
        </>
    );
};

export default observer(ArchivedHandouts);
